import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import ThemeToggle from "@/theme/ThemeToggle";
import AppLauncher from "@/components/AppLauncher";
import Skeleton from "@/components/Skeleton";
import Logo from "@/components/Logo";
import Icon, { type IconName } from "@/components/Icon";
import UserMenu from "@/components/UserMenu";
import ProgressBar from "@/components/ProgressBar";
import { colorVar } from "@/components/CourseContextBar";
import { listExercises } from "@/api/exercises";
import { listEnrolledCourses } from "@/api/course";
import type { ExerciseSummary, EnrolledCourse } from "@/types";

interface ChapterGroup {
  key: string;            // id du chapitre, ou "none"
  title: string;
  order: number;
  series: ExerciseSummary[];
}

const isDone = (e: ExerciseSummary) => e.step_count > 0 && e.completed_steps >= e.step_count;

type Status = { label: string; cta: string; color: string; icon: IconName };

/** Statut pédagogique d'une série (couleur + accroche), pour motiver l'élève. */
function serieStatus(e: ExerciseSummary): Status {
  if (isDone(e))             return { label: "Terminé", cta: "Revoir",    color: "var(--green)",  icon: "done" };
  if (e.completed_steps > 0) return { label: "En cours", cta: "Continuer", color: "var(--orange)", icon: "run" };
  return                           { label: "À faire",  cta: "Commencer", color: "var(--blue)",   icon: "run" };
}

/** Contenu d'un cours : chapitres en accordéons dépliant les cartes de séries.
 *  L'élève arrive ici directement depuis le hub (le cours = son niveau ; pas de
 *  choix de degré, c'est l'enseignant qui l'a inscrit au bon cours). */
export default function CourseExercises() {
  const { courseId: courseIdParam } = useParams<{ courseId: string }>();
  const courseId = Number(courseIdParam);
  const location = useLocation();
  const [exs, setExs]       = useState<ExerciseSummary[] | null>(null);
  const [course, setCourse] = useState<EnrolledCourse | null>(null);
  const [open, setOpen]     = useState<Set<string>>(new Set());

  useEffect(() => {
    listExercises({ course_id: courseId }).then(setExs).catch(() => setExs([]));
    listEnrolledCourses().then((cs) => setCourse(cs.find((c) => c.id === courseId) ?? null)).catch(() => {});
  }, [courseId]);

  const accent = course ? colorVar(course.color) : "var(--blue)";
  const groups = useMemo(() => groupByChapter(exs ?? []), [exs]);

  // Ouvre le chapitre d'origine si on revient d'un exercice (chapterKey passé
  // dans l'état de route), sinon le 1ᵉʳ chapitre non terminé (ou le premier).
  useEffect(() => {
    if (!exs) return;
    const fromChapter = (location.state as { chapterKey?: string } | null)?.chapterKey;
    if (fromChapter && groups.some((g) => g.key === fromChapter)) {
      setOpen(new Set([fromChapter]));
      return;
    }
    const firstIncomplete = groups.find((g) => g.series.some((s) => !isDone(s)));
    const target = firstIncomplete ?? groups[0];
    if (target) setOpen(new Set([target.key]));
  }, [exs]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (key: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <Link to="/exercises" style={styles.logo} title="Mes cours">
          <Icon name="back" size={18} />
          <Logo size={24} />
          <span>Mes cours</span>
        </Link>
        <div style={styles.headerRight}>
          <ThemeToggle />
          <AppLauncher />
          <UserMenu />
        </div>
      </header>

      <main style={styles.main}>
        <h1 style={styles.title}>
          <span style={{ ...styles.titleDot, background: accent }} />
          {course?.title ?? "Programme"}
        </h1>

        {exs === null ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} height={52} />)}
          </div>
        ) : groups.length === 0 ? (
          <p style={styles.hint}>Aucune série publiée dans ce cours pour le moment.</p>
        ) : (
          <div style={styles.chapters}>
            {groups.map((g, gi) => {
              const expanded = open.has(g.key);
              const doneCount = g.series.filter(isDone).length;
              const allDone = doneCount === g.series.length && g.series.length > 0;
              return (
                <div key={g.key} style={styles.chapter}>
                  <button style={styles.chapterHead} onClick={() => toggle(g.key)} aria-expanded={expanded}>
                    <span style={{ ...styles.chapterIndex, ...(allDone ? styles.chapterIndexDone : { background: "var(--badge-bg)", color: accent }) }}>
                      {allDone ? <Icon name="done" size={15} /> : gi + 1}
                    </span>
                    <span style={styles.chapterTitle}>{g.title}</span>
                    <span style={styles.chapterStat}>{doneCount}/{g.series.length} séries</span>
                    <span style={styles.chapterBar}>
                      <ProgressBar value={doneCount} total={g.series.length} hideLabel />
                    </span>
                    <Icon name={expanded ? "expanded" : "collapsed"} size={16} style={{ color: "var(--text-muted)", flexShrink: 0 }} />
                  </button>

                  {expanded && (
                    <div style={styles.series}>
                      {g.series.map((s) => {
                        const st = serieStatus(s);
                        return (
                          <Link
                            key={s.id}
                            to={`/exercises/${s.id}`}
                            state={{ courseId, chapterKey: g.key }}
                            className="card"
                            style={{ ...styles.serieCard, borderLeftColor: st.color }}
                          >
                            <span style={{ ...styles.statusPill, color: st.color }}>
                              <Icon name={st.icon} size={11} /> {st.label}
                            </span>
                            <h3 style={styles.serieTitle}>{s.title}</h3>
                            {s.description && <p style={styles.serieDesc}>{s.description}</p>}
                            <div style={styles.serieFoot}>
                              <ProgressBar value={s.completed_steps} total={s.step_count} unit="exercices" />
                              <span style={{ ...styles.cta, color: st.color }}>
                                {st.cta} <Icon name="next" size={13} />
                              </span>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

function groupByChapter(exs: ExerciseSummary[]): ChapterGroup[] {
  const map = new Map<string, ChapterGroup>();
  const add = (key: string, title: string, order: number, e: ExerciseSummary) => {
    let g = map.get(key);
    if (!g) { g = { key, title, order, series: [] }; map.set(key, g); }
    g.series.push(e);
  };
  for (const e of exs) {
    // Many-to-many : la série apparaît sous CHACUN de ses chapitres ; aucun → « Sans chapitre ».
    if (e.chapters.length === 0) add("none", "Sans chapitre", Number.MAX_SAFE_INTEGER, e);
    else for (const c of e.chapters) add(String(c.id), c.title, c.order ?? 0, e);
  }
  return [...map.values()].sort((a, b) => a.order - b.order);
}

const styles: Record<string, React.CSSProperties> = {
  page: { minHeight: "100vh", background: "var(--bg-base)", color: "var(--text)" },
  header: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "16px 32px", background: "var(--bg-bar)", borderBottom: "1px solid var(--border-soft)",
  },
  logo: { color: "var(--blue)", display: "inline-flex", alignItems: "center", gap: 10, textDecoration: "none" },
  headerRight: { display: "flex", alignItems: "center", gap: 12 },
  main: { padding: "24px 32px", maxWidth: 1000, margin: "0 auto" },
  title: { fontSize: 24, fontWeight: 700, margin: "0 0 24px", color: "var(--text)", display: "flex", alignItems: "center", gap: 12 },
  titleDot: { width: 14, height: 14, borderRadius: "50%", flexShrink: 0 },
  hint: { color: "var(--text-faint)" },
  chapters: { display: "flex", flexDirection: "column", gap: 14 },
  chapter: { background: "var(--bg-panel)", border: "1px solid var(--border-soft)", borderRadius: "var(--radius-lg)", overflow: "hidden" },
  chapterHead: {
    display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left",
    padding: "14px 18px", background: "transparent", border: "none", color: "var(--text)",
    fontSize: 15.5, fontWeight: 600,
  },
  chapterIndex: {
    flexShrink: 0, width: 28, height: 28, borderRadius: "50%", display: "inline-flex",
    alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700,
    background: "var(--badge-bg)", color: "var(--blue)",
  },
  chapterIndexDone: { background: "var(--ok-box-bg)", color: "var(--green)" },
  chapterTitle: { flex: "1 1 auto", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  chapterStat: { flexShrink: 0, fontSize: 13, fontWeight: 600, color: "var(--text)", fontVariantNumeric: "tabular-nums" },
  chapterBar: { width: 120, flexShrink: 0 },
  series: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: 14,
    padding: "6px 18px 18px",
  },
  serieCard: {
    display: "flex", flexDirection: "column", gap: 8, minHeight: 132,
    background: "var(--bg-base)", border: "1px solid var(--border-soft)",
    borderLeft: "3px solid var(--blue)", borderRadius: "var(--radius)",
    padding: "13px 16px", textDecoration: "none", color: "var(--text)",
  },
  statusPill: {
    alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: 4,
    fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5,
  },
  serieTitle: { margin: 0, fontSize: 14.5, color: "var(--text)", lineHeight: 1.3 },
  serieDesc: { margin: 0, fontSize: 12.5, color: "var(--text-muted)", lineHeight: 1.4, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" },
  serieFoot: { marginTop: "auto", display: "flex", flexDirection: "column", gap: 8 },
  cta: { alignSelf: "flex-end", display: "inline-flex", alignItems: "center", gap: 3, fontSize: 12.5, fontWeight: 700 },
};
